import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type ReactNode } from 'react';
import { adminApi } from '../api/admin';
import {
  describeSchedule,
  scheduleApi,
  type Recurrence,
  type ScheduledJob,
  type ScheduleInput,
  type ScheduleSortField,
  type ScheduleType,
} from '../api/schedule';
import { DataListFrame, sortArrow, useListControls } from '../components/data-list';

// 並び替え可能列（backend SCHEDULE_SORT_FIELDS と一致）。
const SORT_OPTIONS: { value: ScheduleSortField; label: string }[] = [
  { value: 'id', label: 'ID' },
  { value: 'name', label: '名前' },
  { value: 'nextRunAt', label: '次回' },
];

// スケジュール = 定時 / 繰り返しで Worker に流すタスクを登録・管理する画面（親タスク #79）。
// 一覧 / 登録 / 編集 / 有効・無効。検索/並び替え/ページングは常にサーバ側（お手本: エージェント一覧）。
export function SchedulesPage() {
  const qc = useQueryClient();
  const invalidate = () => qc.invalidateQueries({ queryKey: ['schedules'] });

  const projectsQuery = useQuery({ queryKey: ['projects'], queryFn: adminApi.listProjects });
  const projects = projectsQuery.data ?? [];
  const projectName = (id: number | null) => {
    if (id == null) return null;
    const p = projects.find((x) => x.id === id);
    return p ? p.nickname || p.name : `#${id}`;
  };

  const controls = useListControls<ScheduleSortField>('konecco.list.schedules', {
    q: '',
    sortBy: 'id',
    sortDir: 'desc',
    page: 1,
    pageSize: 20,
  });
  // マスタ的な項目（対象プロジェクト/種別/状態）= ドロップダウン絞り込み。'' は指定なし。
  const [projectFilter, setProjectFilter] = useState<string>('');
  const [typeFilter, setTypeFilter] = useState<'' | ScheduleType>('');
  const [enabledFilter, setEnabledFilter] = useState<'' | 'true' | 'false'>('');

  const jobsQuery = useQuery({
    queryKey: [
      'schedules',
      'search',
      controls.q,
      projectFilter,
      typeFilter,
      enabledFilter,
      controls.sortBy,
      controls.sortDir,
      controls.page,
      controls.pageSize,
    ],
    queryFn: () =>
      scheduleApi.search({
        q: controls.q || undefined,
        projectId: projectFilter === '' ? undefined : Number(projectFilter),
        scheduleType: typeFilter || undefined,
        enabled: enabledFilter === '' ? undefined : enabledFilter === 'true',
        sortBy: controls.sortBy,
        sortDir: controls.sortDir,
        page: controls.page,
        pageSize: controls.pageSize,
      }),
    placeholderData: keepPreviousData,
  });
  const jobs = jobsQuery.data?.items ?? [];
  const total = jobsQuery.data?.total ?? 0;

  const [createOpen, setCreateOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<ScheduledJob | null>(null);

  const createJob = useMutation({
    mutationFn: scheduleApi.create,
    onSuccess: () => {
      setCreateOpen(false);
      invalidate();
    },
  });
  const updateJob = useMutation({
    mutationFn: (vars: { id: number; input: Partial<ScheduleInput> }) =>
      scheduleApi.update(vars.id, vars.input),
    onSuccess: () => {
      setEditTarget(null);
      invalidate();
    },
  });
  const toggleJob = useMutation({
    mutationFn: (vars: { id: number; enabled: boolean }) =>
      scheduleApi.setEnabled(vars.id, vars.enabled),
    onSuccess: invalidate,
  });
  const deleteJob = useMutation({ mutationFn: scheduleApi.remove, onSuccess: invalidate });

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <p className="k-muted" style={{ margin: 0, fontSize: 13 }}>
        定時（1回）・繰り返しで Worker に流すタスクを登録します。
      </p>
      {jobsQuery.error && (
        <p className="k-error">取得に失敗しました: {(jobsQuery.error as Error).message}</p>
      )}
      {deleteJob.error && (
        <p className="k-error">削除に失敗しました: {(deleteJob.error as Error).message}</p>
      )}

      <DataListFrame
        title="スケジュール"
        total={total}
        loading={jobsQuery.isLoading}
        controls={controls}
        sortOptions={SORT_OPTIONS}
        searchPlaceholder="あいまい検索…（名前・指示文）"
        filters={
          <>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span className="k-muted" style={{ fontSize: 12 }}>
                対象
              </span>
              <select
                value={projectFilter}
                onChange={(e) => {
                  setProjectFilter(e.target.value);
                  controls.setPage(1);
                }}
                style={{ padding: '7px 10px', fontSize: 13 }}
              >
                <option value="">すべて</option>
                {projects.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.nickname || p.name}
                  </option>
                ))}
              </select>
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span className="k-muted" style={{ fontSize: 12 }}>
                種別
              </span>
              <select
                value={typeFilter}
                onChange={(e) => {
                  setTypeFilter(e.target.value as '' | ScheduleType);
                  controls.setPage(1);
                }}
                style={{ padding: '7px 10px', fontSize: 13 }}
              >
                <option value="">すべて</option>
                <option value="one_time">定時（1回）</option>
                <option value="recurring">繰り返し</option>
              </select>
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span className="k-muted" style={{ fontSize: 12 }}>
                状態
              </span>
              <select
                value={enabledFilter}
                onChange={(e) => {
                  setEnabledFilter(e.target.value as '' | 'true' | 'false');
                  controls.setPage(1);
                }}
                style={{ padding: '7px 10px', fontSize: 13 }}
              >
                <option value="">すべて</option>
                <option value="true">有効</option>
                <option value="false">無効</option>
              </select>
            </label>
          </>
        }
        rightActions={
          <button type="button" onClick={() => setCreateOpen(true)}>
            ＋ 新規作成
          </button>
        }
      >
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ textAlign: 'left', borderBottom: '1px solid #e2e8f0' }}>
              <th style={th}>状態</th>
              <th
                style={{ ...th, cursor: 'pointer', userSelect: 'none' }}
                onClick={() => controls.toggleSort('name')}
                title="クリックで並び替え"
              >
                名前{sortArrow(controls, 'name')}
              </th>
              <th style={th}>タイミング</th>
              <th
                style={{ ...th, cursor: 'pointer', userSelect: 'none' }}
                onClick={() => controls.toggleSort('nextRunAt')}
                title="クリックで並び替え"
              >
                次回{sortArrow(controls, 'nextRunAt')}
              </th>
              <th style={th}>対象 / 内容</th>
              <th style={th}>操作</th>
            </tr>
          </thead>
          <tbody>
            {jobs.map((j) => (
              <tr key={j.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                <td style={td}>
                  <button
                    className="btn-ghost"
                    type="button"
                    disabled={toggleJob.isPending}
                    title={j.enabled ? 'クリックで無効化' : 'クリックで有効化'}
                    onClick={() => toggleJob.mutate({ id: j.id, enabled: !j.enabled })}
                    style={{ padding: '2px 8px' }}
                  >
                    {j.enabled ? '🟢 有効' : '⚪ 無効'}
                  </button>
                </td>
                <td style={td}>
                  <strong style={{ opacity: j.enabled ? 1 : 0.5 }}>{j.name}</strong>
                </td>
                <td style={td}>{describeSchedule(j)}</td>
                <td style={{ ...td, color: '#475569', whiteSpace: 'nowrap' }}>
                  {j.nextRunAt ? j.nextRunAt.replace('T', ' ') : <span className="k-muted">—</span>}
                </td>
                <td style={{ ...td, maxWidth: 280 }}>
                  <div style={{ fontSize: 12, color: '#64748b' }}>
                    {projectName(j.projectId) ?? '（ルーティング委譲）'}
                  </div>
                  <span title={j.prompt}>
                    {j.prompt.length > 48 ? `${j.prompt.slice(0, 48)}…` : j.prompt}
                  </span>
                </td>
                <td style={td}>
                  <div style={{ display: 'flex', gap: 6 }}>
                    <button className="btn-ghost" type="button" onClick={() => setEditTarget(j)}>
                      変更
                    </button>
                    <button
                      className="btn-danger"
                      type="button"
                      disabled={deleteJob.isPending}
                      onClick={() => {
                        if (confirm(`スケジュール「${j.name}」を削除しますか？`)) {
                          deleteJob.mutate(j.id);
                        }
                      }}
                    >
                      削除
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </DataListFrame>

      {createOpen && (
        <ScheduleFormDialog
          mode="create"
          projects={projects}
          submitting={createJob.isPending}
          error={createJob.error as Error | null}
          onClose={() => setCreateOpen(false)}
          onSubmit={(v) => createJob.mutate(v)}
        />
      )}
      {editTarget && (
        <ScheduleFormDialog
          mode="edit"
          initial={editTarget}
          projects={projects}
          submitting={updateJob.isPending}
          error={updateJob.error as Error | null}
          onClose={() => setEditTarget(null)}
          onSubmit={(v) => updateJob.mutate({ id: editTarget.id, input: v })}
        />
      )}
    </div>
  );
}

type RecurrenceKind = Recurrence['kind'];
const WEEKDAYS = ['日', '月', '火', '水', '木', '金', '土'];

function ScheduleFormDialog({
  mode,
  initial,
  projects,
  onSubmit,
  onClose,
  submitting,
  error,
}: {
  mode: 'create' | 'edit';
  initial?: ScheduledJob;
  projects: { id: number; name: string; nickname: string | null; company?: { name: string } }[];
  onSubmit: (v: ScheduleInput) => void;
  onClose: () => void;
  submitting: boolean;
  error: Error | null;
}) {
  const [name, setName] = useState(initial?.name ?? '');
  const [scheduleType, setScheduleType] = useState<ScheduleType>(initial?.scheduleType ?? 'recurring');
  const [runAt, setRunAt] = useState(initial?.runAt ?? '');
  const [recKind, setRecKind] = useState<RecurrenceKind>(initial?.recurrence?.kind ?? 'daily');
  const [hour, setHour] = useState(
    initial?.recurrence && 'hour' in initial.recurrence ? initial.recurrence.hour : 9,
  );
  const [minute, setMinute] = useState(
    initial?.recurrence && 'minute' in initial.recurrence ? initial.recurrence.minute : 0,
  );
  const [weekday, setWeekday] = useState(
    initial?.recurrence?.kind === 'weekly' ? initial.recurrence.weekday : 1,
  );
  const [cronExpr, setCronExpr] = useState(
    initial?.recurrence?.kind === 'cron' ? initial.recurrence.expr : '',
  );
  const [projectId, setProjectId] = useState<number | ''>(initial?.projectId ?? '');
  const [prompt, setPrompt] = useState(initial?.prompt ?? '');

  const buildRecurrence = (): Recurrence => {
    switch (recKind) {
      case 'hourly':
        return { kind: 'hourly', minute };
      case 'daily':
        return { kind: 'daily', hour, minute };
      case 'weekly':
        return { kind: 'weekly', weekday, hour, minute };
      case 'cron':
        return { kind: 'cron', expr: cronExpr.trim() };
    }
  };

  const valid =
    name.trim() &&
    prompt.trim() &&
    (scheduleType === 'one_time' ? !!runAt : recKind !== 'cron' || !!cronExpr.trim());

  return (
    <Modal
      title={mode === 'create' ? '新しいスケジュールを登録' : `スケジュールを変更 — #${initial?.id}`}
      onClose={onClose}
    >
      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (!valid) return;
          onSubmit({
            name: name.trim(),
            scheduleType,
            runAt: scheduleType === 'one_time' ? runAt : null,
            recurrence: scheduleType === 'recurring' ? buildRecurrence() : null,
            projectId: projectId === '' ? null : Number(projectId),
            prompt: prompt.trim(),
            enabled: initial?.enabled ?? true,
          });
        }}
        style={{ display: 'flex', flexDirection: 'column', gap: 14 }}
      >
        <div style={fieldWrap}>
          <label style={labelStyle}>名前</label>
          <input
            placeholder="例: 毎朝のデイリーレポート"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            autoFocus
          />
        </div>

        <div style={fieldWrap}>
          <label style={labelStyle}>実行タイプ</label>
          <div style={{ display: 'flex', gap: 8 }}>
            {(
              [
                { v: 'recurring', label: '🔁 繰り返し' },
                { v: 'one_time', label: '🕐 定時（1回）' },
              ] as const
            ).map((o) => (
              <button
                key={o.v}
                type="button"
                className={scheduleType === o.v ? '' : 'btn-ghost'}
                onClick={() => setScheduleType(o.v)}
                style={{ flex: 1 }}
              >
                {o.label}
              </button>
            ))}
          </div>
        </div>

        {scheduleType === 'one_time' ? (
          <div style={fieldWrap}>
            <label style={labelStyle}>実行日時</label>
            <input
              type="datetime-local"
              value={runAt}
              onChange={(e) => setRunAt(e.target.value)}
              required
            />
          </div>
        ) : (
          <div style={fieldWrap}>
            <label style={labelStyle}>繰り返し</label>
            <select value={recKind} onChange={(e) => setRecKind(e.target.value as RecurrenceKind)}>
              <option value="hourly">毎時</option>
              <option value="daily">毎日</option>
              <option value="weekly">毎週</option>
              <option value="cron">cron 式（上級者向け）</option>
            </select>
            <div style={{ display: 'flex', gap: 8, marginTop: 6, alignItems: 'flex-end' }}>
              {recKind === 'weekly' && (
                <div style={{ ...fieldWrap, width: 90 }}>
                  <label style={labelStyle}>曜日</label>
                  <select value={weekday} onChange={(e) => setWeekday(Number(e.target.value))}>
                    {WEEKDAYS.map((w, i) => (
                      <option key={i} value={i}>
                        {w}曜
                      </option>
                    ))}
                  </select>
                </div>
              )}
              {(recKind === 'daily' || recKind === 'weekly') && (
                <div style={{ ...fieldWrap, width: 80 }}>
                  <label style={labelStyle}>時</label>
                  <select value={hour} onChange={(e) => setHour(Number(e.target.value))}>
                    {Array.from({ length: 24 }, (_, i) => (
                      <option key={i} value={i}>
                        {String(i).padStart(2, '0')}
                      </option>
                    ))}
                  </select>
                </div>
              )}
              {recKind !== 'cron' && (
                <div style={{ ...fieldWrap, width: 80 }}>
                  <label style={labelStyle}>分</label>
                  <select value={minute} onChange={(e) => setMinute(Number(e.target.value))}>
                    {Array.from({ length: 60 }, (_, i) => (
                      <option key={i} value={i}>
                        {String(i).padStart(2, '0')}
                      </option>
                    ))}
                  </select>
                </div>
              )}
              {recKind === 'cron' && (
                <div style={{ ...fieldWrap, flex: 1 }}>
                  <label style={labelStyle}>cron 式（分 時 日 月 曜）</label>
                  <input
                    placeholder="例: 0 9 * * 1-5"
                    value={cronExpr}
                    onChange={(e) => setCronExpr(e.target.value)}
                  />
                </div>
              )}
            </div>
          </div>
        )}

        <div style={fieldWrap}>
          <label style={labelStyle}>対象プロジェクト（未指定ならルーティングに委譲）</label>
          <select
            value={projectId}
            onChange={(e) => setProjectId(e.target.value ? Number(e.target.value) : '')}
          >
            <option value="">未指定（ルーティング委譲）</option>
            {projects.map((p) => (
              <option key={p.id} value={p.id}>
                {p.company?.name ? `${p.company.name} / ` : ''}
                {p.nickname || p.name}
              </option>
            ))}
          </select>
        </div>

        <div style={fieldWrap}>
          <label style={labelStyle}>流す内容（Worker への指示文）</label>
          <textarea
            placeholder="例: 今日の進捗を要約して #report チャンネルに投稿して"
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            rows={4}
            required
          />
        </div>

        {error && <p className="k-error">{error.message}</p>}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button className="btn-ghost" type="button" onClick={onClose}>
            キャンセル
          </button>
          <button type="submit" disabled={submitting || !valid}>
            {mode === 'create' ? '登録' : '変更を保存'}
          </button>
        </div>
      </form>
    </Modal>
  );
}

function Modal({
  title,
  onClose,
  children,
  width = 460,
}: {
  title: string;
  onClose: () => void;
  children: ReactNode;
  width?: number;
}) {
  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(15,23,42,0.45)',
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'center',
        padding: '7vh 16px',
        zIndex: 50,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="k-card"
        style={{ width: '100%', maxWidth: width, maxHeight: '85vh', overflow: 'auto' }}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: 14,
          }}
        >
          <h3 style={{ margin: 0 }}>{title}</h3>
          <button className="btn-ghost" type="button" onClick={onClose} aria-label="閉じる">
            ✕
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

const th = { padding: '8px 12px', fontWeight: 600, fontSize: 13 } as const;
const td = { padding: '8px 12px', fontSize: 14, verticalAlign: 'top' } as const;
const labelStyle = { fontSize: 13, fontWeight: 600, color: '#334155' } as const;
const fieldWrap = { display: 'flex', flexDirection: 'column', gap: 4 } as const;
